import { useCallback, useEffect, useMemo, useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import useSWR from 'swr';
import { Stack } from '@mui/material';

import { isEqual } from 'lodash';
import { mutateRolesByName } from '@modules/ContactManager';
import { getListsDifference, getOptions } from '@shared/commonFunctions';
import { SaveButton } from '@shared/components/SaveButton';
import { Loader } from '@shared/enhancers/withLoader';
import { useQueryState } from '@shared/hooks/useQueryState';
import { Select } from '@shared/inputs/Select';
import { RelativeWrapper } from '@shared/styledComponents';
import { TeamManagerColumn, TeamManagerContent } from '@styles/Admin-tools/TeamManagerStyle';
import { SERVICES } from '@shared/constants/apiConfig';

import { ITeamManagerTabsProps } from '..';
import { AddTeamButton } from './components/AddTeamButton';
import { EditTeamModal } from './components/EditTeamModal';
import { Managers } from './components/Managers';
import { UnassignedAgents } from './components/UnassignedAgents';
import { IUnassignedUsersResponse, NTeamManager, TeamManagerModel, TeamModel } from './interfaces';
import { prepareUpdateRequest, requestDeleteTeam, requestUpdateTeam } from './queries';
import { Team } from './Team';
import { onTeamManagerDragEnd } from './utils';

export const Teams = ({ periods, teamTypes }: ITeamManagerTabsProps) => {
  const [periodId, setPeriodId] = useQueryState('periodId', '');
  const [teamTypeId, setTeamTypeId] = useQueryState('teamTypeId', '');
  const [teamToEdit, setTeamToEdit] = useState<NTeamManager.Team | null>(null);
  const [editTeamModalPosition, setEditTeamModalPosition] = useState(0);
  const [teamManager, setTeamManager] = useState<NTeamManager.Response>(new TeamManagerModel());
  const [visibleAgents, setVisibleAgents] = useState<NTeamManager.User[]>([]);
  const [showValidation, setShowValidation] = useState(false);

  const [loading, setLoading] = useState(false);
  const {
    data,
    error,
    isValidating,
    mutate: refreshTeamManager,
  } = useSWR<NTeamManager.Response>(teamTypeId && periodId && `/${SERVICES.USER}/teams/types/${teamTypeId}/${periodId}`, {
    revalidateIfStale: true,
  });
  const { data: unassigned } = useSWR<IUnassignedUsersResponse>(
    data && !data?.unassignedUsers.length && `/${SERVICES.USER}/users/unassigned`
  );

  useEffect(() => {
    const currentDate = new Date(); // This gets the current date and time
    const currentYear = currentDate.getFullYear(); // This gets the current year
    const currentMonth = currentDate.getMonth() + 1;
    const period = periods.find((a) => a.name == `${currentYear}-${currentMonth}`);
    !periodId && setPeriodId(period?.id);
    !teamTypeId && setTeamTypeId(teamTypes[0]?.id);
  }, [periods, teamTypes]);

  useEffect(() => {
    if (data) {
      setTeamManager(
        new TeamManagerModel({
          ...data,
          unassignedUsers: data.unassignedUsers || unassigned?.unassignedUsers,
        })
      );
    }
  }, [data, unassigned]);

  const shouldDisplayAddButton = (teamIndex: number) => Boolean(teamManager.teams && teamIndex === teamManager.teams.length - 1);

  const onAddTeam = useCallback(() => {
    const newTeam = new TeamModel({
      index: teamManager.teams?.length ?? 0,
      periodId,
      teamTypeId,
    });
    setShowValidation(false);
    setTeamToEdit({ ...newTeam, isNew: true });
    setEditTeamModalPosition((prevState) => prevState + 1);
  }, [teamManager.teams, periodId, teamTypeId]);

  const applyTeamEdits = useCallback(
    (editedTeam: NTeamManager.Team) => {
      if (!editedTeam.name || !editedTeam.periodId || !editedTeam.teamTypeId) {
        setShowValidation(true);
        return;
      }
      const isNewTeam = editedTeam?.isNew;
      setTeamManager({
        ...teamManager,
        teams:
        isNewTeam
        ? [...teamManager.teams, editedTeam]
        : teamManager.teams.map((team, index) => (index !== editedTeam.index ? team : editedTeam)),
      });
      setShowValidation(false);
      setTeamToEdit(null);
      setEditTeamModalPosition(0);
    }, [teamManager.teams, setTeamManager]);

  const handleDeleteTeam = useCallback (() => {
    if (teamToEdit) {
      if (teamToEdit.id) {
        requestDeleteTeam(teamToEdit.id);
        refreshTeamManager();
      }
      const teams = teamManager.teams
        .filter((a) => a.index != teamToEdit.index)
        ?.map((team, index) => {
          const newTeam = new TeamModel(team);
          newTeam.index = index;
          return newTeam;
        });
      setTeamManager({
        ...teamManager,
        teams: teams,
      });
      setTeamToEdit(null);
      setShowValidation(false);
      setEditTeamModalPosition(0);
    }
  }, [teamManager, teamToEdit]);

  const onSubmit = useCallback(async () => {
    const modifiedManagers = getListsDifference<NTeamManager.User>(data?.managers || [], teamManager?.managers, 'userId');
    setLoading(true);
    const request = prepareUpdateRequest(teamTypeId, periodId, teamManager, modifiedManagers);

    await requestUpdateTeam(request);
    refreshTeamManager();
    mutateRolesByName();
    setLoading(false);
  }, [
    data,
    teamManager,
    teamTypeId,
    periodId,
  ]);

  const handleOnEdit = useCallback((team: NTeamManager.Team) => {
    setTeamToEdit({ ...team, isNew: false });
    setEditTeamModalPosition((prevState) => prevState + 1);
  }, []);

  const handleClose = useCallback(() => {
    setShowValidation(false);
    setTeamToEdit(null);
    setEditTeamModalPosition(0);
  }, []);
  
  const filteredUnAssigedUsers = useMemo(
    () => teamManager.unassignedUsers.filter((agent) => !teamManager.managers.includes(agent)), [teamManager]
  );

  const isSaveButtonDisabled = useMemo(() => isEqual(new TeamManagerModel(data), teamManager),
  [data, teamManager]);

  const isLoading = useMemo(() => ((teamTypeId && periodId) && (!data && !error) || isValidating), [isValidating, error, data, teamTypeId, periodId]);
  
  return !periods?.length || !teamTypes.length || isLoading ? (
    <Loader />
  ) : (
    <RelativeWrapper>
      <SaveButton
        displayMessage="You have unsaved changes"
        disabled={isSaveButtonDisabled}
        isLoading={loading}
        onClick={onSubmit}
      />

      <TeamManagerContent>
        <DragDropContext onDragEnd={(result) => onTeamManagerDragEnd(result, visibleAgents, teamManager, setTeamManager)}>
          <TeamManagerColumn>
            <Select
              label="Period"
              value={periodId}
              options={getOptions(periods)}
              placeholder="Select Period..."
              onChange={setPeriodId}
            />

            <Managers
              value={teamManager.managers}
              data={filteredUnAssigedUsers}
              onChange={(managers) => setTeamManager({ ...teamManager, managers })}
            />
          </TeamManagerColumn>

          <TeamManagerColumn>
            <Select
              label="Team Type"
              value={teamTypeId}
              options={getOptions(teamTypes)}
              placeholder="Select Team Type..."
              onChange={setTeamTypeId}
            />

            <UnassignedAgents
              data={filteredUnAssigedUsers}
              agents={visibleAgents.filter((agent) => !teamManager.managers.includes(agent))}
              setAgents={setVisibleAgents}
            />
          </TeamManagerColumn>

            <Stack flexDirection="row" gap={2} sx={{overflow: 'auto'}}>
              {teamManager.teams.length ? (
                teamManager.teams.map((team, index) => (
                  <TeamManagerColumn
                    key={index}
                    minWidth='200px'
                    height="70vh"
                  >
                    <Team
                      team={team}
                      index={index}
                      displayAddButton={shouldDisplayAddButton(index)}
                      onAddTeam={onAddTeam}
                      onEditTeam={() => handleOnEdit(team)}
                    />
                  </TeamManagerColumn>
                ))
              ) : (
                <AddTeamButton
                  sx={{ mt: '5px' }}
                  onAddTeam={onAddTeam}
                />
              )}
            </Stack>
        </DragDropContext>
      </TeamManagerContent>

      {teamToEdit && (
        <EditTeamModal
          team={teamToEdit}
          editTeamModalPosition={editTeamModalPosition}
          teamTypes={teamTypes}
          periods={periods}
          showValidation={showValidation}
          handleDelete={handleDeleteTeam}
          handleSubmit={applyTeamEdits}
          handleClose={handleClose}
        />
      )}
    </RelativeWrapper>
  );
};
